/** Javascript & React helper methods - Copyright (c) Cupla Software */

import React from "react";

Math.toRadians = deg => (deg * Math.PI) / 180;
Math.toDegrees = rad => (rad / Math.PI) * 180;

/** Gets element rect relative to its parent */
export function getElementRect(elem, inScrollArea) {
    let r = elem.getBoundingClientRect();
    let rr = elem.parentNode ? elem.parentNode.getBoundingClientRect() : r;
    let st = 0;
    let sl = 0;
    if (inScrollArea) {
        st = elem.parentNode.scrollTop;
        sl = elem.parentNode.scrollLeft;
    }
    return {
        left: r.left - rr.left + sl,
        top: r.top - rr.top + st,
        right: r.right - rr.left + sl,
        bottom: r.bottom - rr.top + st
    };
}

/** Gets element rect relative to display */
export function getElementDisplayRect(elem) {
    let r = elem.getBoundingClientRect();
    let rr = document.getElementById("display").getBoundingClientRect();
    return {
        left: r.left - rr.left,
        top: r.top - rr.top,
        right: r.right - rr.left,
        bottom: r.bottom - rr.top
    };
}

export function div() {
    return document.createElement("div");
}

export function removeAllChildren(elem) {
    while (elem.lastChild) elem.removeChild(elem.lastChild);
}

export function post(f, t) {
    return setTimeout(f, t);
}

export function dlog(m) {
    if (window.console) console.log(m);
    if (window.app && window.app.debugLevel >= 2) window.app.logDebugView(m);
    return m;
}

/** Gets current time as epoch (ms) */
export function currentTime() {
    return Date.now();
}

let nextKey = 1;

export function key() {
    return nextKey++;
}

export function nbsp(s) {
    return s ? s : "\u00A0";
}

export function url(s) {
    return "url(" + s + ")";
}

export function padzero(s, n) {
    s = String(s);
    while (s.length < n) s = "0" + s;
    return s;
}

export function hex(n) {
    let s = Number(n).toString(16);
    if (s.length < 2) s = "0" + s;
    return s;
}

export function parseValidString(s, def) {
    return typeof s !== "undefined" && s !== null
        ? String(s)
        : typeof def !== "undefined"
        ? def
        : "";
}

export function parseValidNumber(n, def) {
    return typeof n !== "undefined" && !isNaN(n) && n !== null
        ? Number(n)
        : typeof def !== "undefined"
        ? def
        : 0;
}

export function parseValidBoolean(n, def) {
    return typeof n !== "undefined" && !isNaN(n) && n !== null
        ? Boolean(n)
        : typeof def !== "undefined"
        ? def
        : false;
}

/** Maps value n from range [a,b] to range [c,d] */
export function mapvalue(n, a, b, c, d, enforce) {
    // Check source range
    if (b === a) {
        // Stupid to call this way, returning always max (the "100%" case)
        return d;
    }

    // Map value (works even if ranges are negative or value goes out of the range)
    n = c + ((n - a) / (b - a)) * (d - c);

    // Enforce value inside target range
    if (enforce) {
        if (d >= c) n = minmax(c, n, d);
        else n = minmax(d, n, c);
    }

    // Done
    return n;
}

export function minmax(mina, a, maxa) {
    return a < mina ? mina : a > maxa ? maxa : a;
}

export function notnull(n) {
    return n ? n : null;
}

export function getByID(array, id) {
    return notnull(array.find(elem => elem.id === id));
}

export function getBy(array, field, value) {
    return notnull(array.find(elem => elem[field] === value));
}

export function removeOf(array, elem) {
    let i = array.indexOf(elem);
    if (i >= 0) {
        array.splice(i, 1);
        return true;
    }
    return false;
}

export class DebugTime {
    time = currentTime();

    delta() {
        let time = currentTime();
        dlog(time - this.time);
        this.time = time;
    }
}

/** Gets date as visual local time string */
export function getDateString(dateOrTime) {
    if (dateOrTime) {
        if (!(dateOrTime instanceof Date)) {
            dateOrTime = new Date(dateOrTime);
        }
        return (
            padzero(dateOrTime.getFullYear(), 4) +
            "-" +
            padzero(dateOrTime.getMonth() + 1, 2) +
            "-" +
            padzero(dateOrTime.getDate(), 2)
        );
    }
    return "";
}

/** Gets date and time as visual local time string */
export function getDateTimeString(dateOrTime, includeMilliseconds) {
    if (dateOrTime) {
        if (!(dateOrTime instanceof Date)) {
            dateOrTime = new Date(dateOrTime);
        }
        return (
            padzero(dateOrTime.getFullYear(), 4) +
            "-" +
            padzero(dateOrTime.getMonth() + 1, 2) +
            "-" +
            padzero(dateOrTime.getDate(), 2) +
            " " +
            padzero(dateOrTime.getHours(), 2) +
            ":" +
            padzero(dateOrTime.getMinutes(), 2) +
            ":" +
            padzero(dateOrTime.getSeconds(), 2) +
            (includeMilliseconds
                ? "." + padzero(dateOrTime.getMilliseconds(), 3)
                : "")
        );
    }
    return "";
}

/** Gets time as visual local time string */
export function getTimeString(dateOrTime, includeMilliseconds) {
    if (dateOrTime) {
        if (!(dateOrTime instanceof Date)) {
            dateOrTime = new Date(dateOrTime);
        }
        return (
            padzero(dateOrTime.getHours(), 2) +
            ":" +
            padzero(dateOrTime.getMinutes(), 2) +
            ":" +
            padzero(dateOrTime.getSeconds(), 2) +
            (includeMilliseconds
                ? "." + padzero(dateOrTime.getMilliseconds(), 3)
                : "")
        );
    }
    return "";
}

/** Parses local time string */
export function parseDateTimeString(str) {
    // "0000-00-00 00:00:00" or "0000-00-00 00:00"
    str = str.trim();
    let ds = str.split(/\D/).map(s => parseInt(s));
    if (ds.length < 5) {
        if ((ds.length === 2 || ds.length === 3) && ds[0] <= 23) {
            // "00:00:00" or "00:00"
            let now = new Date();
            ds.splice(
                0,
                0,
                now.getFullYear(),
                now.getMonth() + 1,
                now.getDate()
            );
        } else if (ds.length === 3 && ds[0] >= 1900) {
            // "0000-00-00"
        } else {
            return null;
        }
    }
    ds[1] = ds[1] - 1; // month
    return new Date(...ds);
}

export function getDurationStringMin(mins) {
    let s = "";
    if (mins >= 60) {
        let hours = Math.floor(mins / 60);
        if (hours >= 24) {
            s = Math.floor(hours / 24) + "pv";
        } else if (hours < 2) {
            s += " " + (mins % 60) + "min";
        } else {
            s = hours + "h";
        }
    } else {
        s = mins + "min";
    }
    return s;
}

export function sortFunction(field) {
    return (a, b) => compare(a[field], b[field]);
}

export function sortFunctionNumber(field) {
    return (a, b) => compare(parseInt(a[field]), parseInt(b[field]), true);
}

export function compare(a, b, nan) {
    if (nan && (isNaN(a) || isNaN(b))) {
        if (isNaN(a) && !isNaN(b)) return -1;
        if (!isNaN(a) && isNaN(b)) return +1;
        return 0;
    }
    if (a < b) return -1;
    if (a > b) return +1;
    return 0;
}

/** React holder class */
export class ReactHolder {
    /** React element */
    element = null;

    /** Instance of created component or HTMLElement */
    instance = null;

    /** ReactHolder(<MyComponent/>)
     *  ReactHolder(<div/>)
     */
    constructor(reactElement) {
        this.element = React.cloneElement(reactElement, {
            ref: e => (this.instance = e)
        });
    }

    render() {
        return this.element;
    }
}

/** Asynchronous sleep */
export async function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
